import { useCallback, useEffect, useRef, useState } from 'react'

import { PublicReadError, type PublicReadFailureKind } from '@pushcaster/api/public-read'

import { fetchSystemStatus, type SystemStatus } from './public-status'

/*
 * The status poll, and the place V1_2_SPEC.md §5.3 is enforced rather than
 * remembered.
 *
 * §5.3's requirement is easy to state and easy to get wrong: a control-plane
 * failure must never be read as "everything is operational". The wrong
 * implementation is not the obvious one — nobody writes `catch { setOperational }`.
 * The wrong implementation is the ordinary one: keep the last successful
 * payload on screen and show a small error somewhere. That page reads
 * "All systems operational, updated 40 minutes ago" for the entire duration of
 * an outage, and every word of it is technically true.
 *
 * ── So staleness demotes, and it demotes to `unknown` ───────────────────────
 *
 * `DESIGN_SYSTEM.md` §7.18 already fixed the threshold for every other surface
 * in this product — a panel older than **two refresh intervals** is stale — and
 * this reuses it rather than inventing a status-page-specific rule. Past that
 * point every component and region state is replaced by `unknown`, the overall
 * line follows, and the payload's own `updated_at` is what the page shows. The
 * names, descriptions and incident list are kept, because those are facts about
 * the world that do not go stale the way a *state* does: an incident that was
 * open sixty seconds ago is still worth reading about.
 *
 * One failed poll therefore changes nothing visible except the freshness bar.
 * Two consecutive failures demote the page. That is deliberate — a page that
 * flickered to "not reporting" on every transient blip would train its reader to
 * ignore the one time it meant it.
 *
 * ── Staleness is measured on the reader's clock, not the server's ──────────
 *
 * The obvious source for "how old is this" is the payload's own `updated_at`,
 * and it is the wrong one. It puts the demotion at the mercy of clock skew
 * between the control plane and the visitor's machine, and both directions
 * fail badly: a server a few minutes behind makes every fresh payload look
 * stale on arrival, so a healthy platform reads "not reporting" permanently;
 * a server a few minutes ahead makes a dead API look fresh, which is §5.3's
 * failure walking back in through the clock. Neither is hypothetical during
 * the infrastructure trouble this page exists for.
 *
 * So the age is `Date.now()` minus **when this browser last received a
 * successful answer** — entirely local, and the question actually being asked:
 * how long since we could reach the API. `updated_at` is still what the page
 * *shows*, because §7.17 requires the instant and the server's is the true one.
 *
 * ── The failure is carried beside the data, not instead of it ──────────────
 *
 * `failure` and `status` are independent: there is data and no failure (normal),
 * data and a failure (a refresh failed — demote when stale), or no data and a
 * failure (the first load failed — §5.3's unavailable condition). A discriminated
 * union would force the third shape onto the second and lose the distinction the
 * page needs.
 */

/** §7.18's interval. 15 s is the endpoint's own `max-age`, so a shorter poll
 *  would mostly re-read the edge's copy of what it already has. */
const REFRESH_MS = 30_000
/** §7.18: "a stale panel (older than two intervals)". */
const STALE_AFTER_MS = REFRESH_MS * 2

export interface StatusFailure {
  readonly kind: PublicReadFailureKind
  /** HTTP status, on `server` only. Never a stack trace, never a field name. */
  readonly status: number | null
}

export interface StatusResult {
  /** `null` until the first successful read. Never invented. */
  readonly status: SystemStatus | null
  /** The most recent failure, if the latest attempt failed. */
  readonly failure: StatusFailure | null
  /** True while a request is outstanding. Used for the retry control only —
   *  the page never blocks on it. */
  readonly loading: boolean
  /** True once `status` is older than two refresh intervals. When this is set,
   *  every state in `status` has already been demoted to `unknown`. */
  readonly stale: boolean
  readonly retry: () => void
}

/**
 * Every state replaced by `unknown`, everything else kept.
 *
 * Written as a transformation of the payload rather than as a flag the renderer
 * checks, so that there is no code path on which a demoted payload can still
 * render a green row. The renderer cannot tell the difference, which is the
 * point: it has one job and it is not this one.
 */
function demoted(status: SystemStatus): SystemStatus {
  return {
    ...status,
    overall: 'unknown',
    components: status.components.map((entry) => ({ ...entry, state: 'unknown' as const })),
    regions: status.regions.map((entry) => ({ ...entry, state: 'unknown' as const })),
  }
}

export function useSystemStatus(): StatusResult {
  const [status, setStatus] = useState<SystemStatus | null>(null)
  /** Local `Date.now()` at the last successful read. See the note on clock skew. */
  const [receivedAt, setReceivedAt] = useState<number | null>(null)
  const [failure, setFailure] = useState<StatusFailure | null>(null)
  const [loading, setLoading] = useState(true)
  /* Advances on a timer so that staleness is re-evaluated even when no request
     is in flight — a page left open through an outage has to demote on its own,
     without waiting for a poll that is failing. */
  const [now, setNow] = useState(() => Date.now())

  const inFlight = useRef<AbortController | null>(null)

  const load = useCallback(() => {
    inFlight.current?.abort()
    const controller = new AbortController()
    inFlight.current = controller
    setLoading(true)

    fetchSystemStatus(controller.signal)
      .then((fresh) => {
        if (controller.signal.aborted) return
        setStatus(fresh)
        setFailure(null)
        const arrived = Date.now()
        setReceivedAt(arrived)
        setNow(arrived)
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setFailure(
          error instanceof PublicReadError
            ? { kind: error.kind, status: error.status }
            : /* Anything that is not a `PublicReadError` escaped the reader,
                 which means it is a bug in this bundle rather than a condition
                 of the network. It is still reported as a failure — the page
                 must not silently show nothing — and `network` is the honest
                 label for "we did not get an answer". */
              { kind: 'network', status: null },
        )
      })
      .finally(() => {
        if (controller.signal.aborted) return
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    load()
    const poll = setInterval(load, REFRESH_MS)
    /* Half the refresh interval, so the transition into "stale" is never more
       than 15 s late. Cheap: it sets a number. */
    const tick = setInterval(() => {
      setNow(Date.now())
    }, REFRESH_MS / 2)

    /*
     * Re-read the moment the tab is looked at again.
     *
     * A background tab's `setInterval` is throttled to roughly once a minute,
     * so a status page left open in another tab is exactly as stale as the
     * browser decided to let it be — measured in a real browser, where a
     * backgrounded page took an extra minute to notice the API had stopped
     * answering. That is the wrong minute to be wrong in, and it is also the
     * common way this page is used: it is opened, left, and come back to when
     * something breaks.
     *
     * So visibility is treated as the signal it is. Coming back to the tab
     * fetches; the timers keep their own pace underneath.
     */
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setNow(Date.now())
        load()
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      clearInterval(poll)
      clearInterval(tick)
      document.removeEventListener('visibilitychange', onVisible)
      inFlight.current?.abort()
    }
  }, [load])

  const stale = receivedAt !== null && now - receivedAt > STALE_AFTER_MS

  return {
    status: status === null ? null : stale ? demoted(status) : status,
    failure,
    loading,
    stale,
    retry: load,
  }
}
